import { useState, useEffect, useCallback, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { logError, addBreadcrumb } from '../utils/sentry-logger';
import { sleep } from '../utils/common';
import {
    STRIPE_LOAD_RETRY_LIMIT,
    STRIPE_LOAD_RETRY_DELAYS_MS,
    STRIPE_LOAD_MAX_DELAY_MS,
    STRIPE_AD_BLOCKER_LOAD_TIME_THRESHOLD_MS,
    STRIPE_AD_BLOCKER_RETRY_DELAY_MS,
} from '../utils/constants';

// Stripe public key from environment (evaluated lazily for testing)
const getStripePublicKey = () => process.env.REACT_APP_STRIPE_PK;

// Constants for logging and error tracking
const STRIPE_LOADER_SOURCE_TAG = 'use-stripe-loader';

// Global cache for the Stripe instance to avoid multiple loads
/** @type {import('@stripe/stripe-js').Stripe | null} */
let stripeInstanceCache = null;
/** @type {Promise<import('@stripe/stripe-js').Stripe> | null} */
let loadingPromise = null;
/** @type {Error | null} */
let stripeErrorCache = null;
/** @type {boolean} */
let hasFinallyFailed = false;

// Error categorization patterns for better Stripe loading failure classification
const AD_BLOCKER_ERROR_PATTERNS = [
    'err_blocked_by_client',
    'blocked by client',
    'blocked by content blocker', // Safari specific
    'net::err_blocked',
    'blocked_by_response', // Chrome specific
    'blocklisted', // Firefox specific
];

const NETWORK_ERROR_PATTERNS = [
    'net::err_internet_disconnected',
    'net::err_network_changed',
    'net::err_connection',
    'net::err_name_not_resolved',
    'network_error',
    'failed to fetch',
    'networkerror',
];

const SECURITY_ERROR_PATTERNS = [
    'net::err_cert',
    'ssl_error',
    'tls_error',
    'csp',
    'content security policy',
    'insecure',
    'mixed content',
];

/**
 * Categorize the error type for better Sentry reporting
 * @param {Error} error - The error to categorize
 * @returns {string} The error category
 */
const categorizeError = (error) => {
    const errorMessage = error?.message?.toLowerCase() || '';

    if (AD_BLOCKER_ERROR_PATTERNS.some((pattern) => errorMessage.includes(pattern))) {
        return 'ad_blocker';
    }
    if (NETWORK_ERROR_PATTERNS.some((pattern) => errorMessage.includes(pattern))) {
        return 'network';
    }
    if (SECURITY_ERROR_PATTERNS.some((pattern) => errorMessage.includes(pattern))) {
        return 'security';
    }
    if (errorMessage.includes('timeout')) {
        return 'timeout';
    }
    return 'unknown';
};

/**
 * Check if an error is caused by an ad blocker
 * @param {Error} error - The error to check
 * @param {number} loadTime - Time taken to fail in milliseconds
 * @returns {boolean} True if likely blocked by ad blocker
 */
const isAdBlockerError = (error, loadTime) => {
    // Ad blockers typically fail instantly (< 50ms)
    const failedTooQuickly = loadTime < STRIPE_AD_BLOCKER_LOAD_TIME_THRESHOLD_MS;

    // Check for specific ad blocker error patterns
    const errorMessage = error?.message?.toLowerCase() || '';
    const hasAdBlockerPattern = AD_BLOCKER_ERROR_PATTERNS.some((pattern) =>
        errorMessage.includes(pattern),
    );

    // Additional check: Failed to load the script at all (common with ad blockers)
    const isScriptLoadError = errorMessage.includes('script') && errorMessage.includes('load');

    // If it has specific blocker patterns OR failed instantly with script load error
    return hasAdBlockerPattern || (failedTooQuickly && isScriptLoadError);
};

/**
 * Create a user-friendly error for ad blocker detection
 * @returns {Error} An error with a clear message and ad blocker flag
 */
const createAdBlockerError = () => {
    const error = new Error(
        'Payment processing is blocked by your ad blocker. Please disable it for this site to continue.',
    );
    error.isAdBlocker = true;
    return error;
};

/**
 * Custom hook for loading Stripe.js with retry logic and error handling
 * Fixes BMA-73V by implementing lazy loading and graceful error recovery
 *
 * @example
 * // Automatic loading on mount (default)
 * const { stripe, loading, error } = useStripeLoader();
 *
 * if (loading) return <Spinner />;
 * if (error) return <ErrorMessage error={error} />;
 *
 * return <Elements stripe={stripe}>...</Elements>;
 *
 * @example
 * // Manual loading on user interaction
 * const { stripe, loading, error, loadStripe } = useStripeLoader({ loadOnMount: false });
 *
 * const handlePaymentClick = () => {
 *   loadStripe(); // Trigger loading only when needed
 * };
 *
 * if (error) return <button onClick={loadStripe}>Retry loading payment</button>;
 *
 * @param {object} options
 * @param {number} [options.retryLimit] - Maximum number of retries (default: STRIPE_LOAD_RETRY_LIMIT)
 * @param {number[]} [options.retryDelays] - Array of retry delays in ms (default: STRIPE_LOAD_RETRY_DELAYS_MS)
 * @param {boolean} [options.loadOnMount] - Whether to load immediately on mount (default: true)
 *
 * @returns {object} { stripe, loading, error, loadStripe }
 */
export default function useStripeLoader({
    retryLimit = STRIPE_LOAD_RETRY_LIMIT,
    retryDelays = STRIPE_LOAD_RETRY_DELAYS_MS,
    loadOnMount = true,
} = {}) {
    const [stripe, setStripe] = useState(stripeInstanceCache);
    const hasNeitherCachedStripeNorError = !stripeInstanceCache && !stripeErrorCache;
    const shouldStartLoading = loadOnMount && hasNeitherCachedStripeNorError;
    const [loading, setLoading] = useState(shouldStartLoading);
    const [error, setError] = useState(stripeErrorCache);
    const mountedRef = useRef(true);

    /**
     * Helper to safely update state (only if component is still mounted)
     */
    const safeSetState = useCallback(
        /**
         * @param {import('@stripe/stripe-js').Stripe | null} newStripe - The Stripe instance or null
         * @param {boolean} newLoading - Loading state
         * @param {Error | null} newError - Error object or null
         */
        (newStripe, newLoading, newError) => {
            if (!mountedRef.current) {
                return;
            }
            setStripe(newStripe);
            setLoading(newLoading);
            setError(newError);
        },
        [],
    );

    /**
     * Helper to calculate retry delay
     */
    const getRetryDelay = useCallback(
        /**
         * @param {number} attemptNumber - The attempt number (0-indexed)
         * @returns {number} The delay in milliseconds before next retry
         */
        (attemptNumber) => {
            return retryDelays[attemptNumber] || STRIPE_LOAD_MAX_DELAY_MS;
        },
        [retryDelays],
    );

    /**
     * Centralized logging helper for all Stripe loading events
     */
    const logStripeEvent = useCallback(
        /**
         * @param {string} event - The event name/message
         * @param {object} data - Additional data to log
         * @param {string} [level='info'] - Log level (info, warning, error)
         * @param {boolean} [isException=false] - Whether to log as exception vs breadcrumb
         */
        (event, data, level = 'info', isException = false) => {
            const logData = {
                ...data,
                stripe_pk_exists: !!getStripePublicKey(),
                url: window.location.href,
            };

            if (isException && data.error) {
                logError(data.error, {
                    tags: {
                        source: STRIPE_LOADER_SOURCE_TAG,
                        stripe_load_failure: true,
                    },
                    contexts: {
                        stripe_load_error: logData,
                    },
                });
            } else {
                addBreadcrumb({
                    message: event,
                    level,
                    data: logData,
                });
            }
        },
        [],
    );

    /**
     * Check if loading should be skipped due to cached state (success/failure) or existing promise
     * Async because it needs to await any existing loading promise
     * @param {boolean} isInitialAttempt - Whether this is the first attempt
     * @returns {Promise<boolean>} True if loading should be skipped (state already set), false to continue loading
     */
    const checkShouldSkipLoading = useCallback(
        async (isInitialAttempt) => {
            if (!isInitialAttempt) {
                return false; // Continue with normal loading for retries
            }

            // Skip if cached instance available
            if (stripeInstanceCache) {
                safeSetState(stripeInstanceCache, false, null);
                return true; // Skip loading
            }

            // Skip if finally failed
            if (hasFinallyFailed) {
                safeSetState(null, false, stripeErrorCache);
                return true; // Skip loading
            }

            // Wait for existing loading promise
            if (loadingPromise) {
                try {
                    const instance = await loadingPromise;
                    safeSetState(instance, false, null);
                    return true; // Skip loading, we got the instance
                } catch (err) {
                    // Clear the failed promise to prevent multiple components from retrying
                    // simultaneously. This ensures only one component initiates the retry.
                    loadingPromise = null;

                    // If we already have a final failure cached, don't continue retrying
                    if (hasFinallyFailed && stripeErrorCache) {
                        safeSetState(null, false, stripeErrorCache);
                        return true; // Skip loading
                    }

                    // Continue with retry logic below
                }
            }

            return false; // Continue with normal loading
        },
        [safeSetState],
    );

    /**
     * Handle ad blocker error detection and state management
     * @param {Error} err - The error that occurred
     * @param {number} loadTime - Time taken for the load attempt
     * @returns {boolean} True if this was an ad blocker error (should not retry)
     */
    const handleAdBlockerError = useCallback(
        (err, loadTime) => {
            if (!isAdBlockerError(err, loadTime)) {
                return false; // Not an ad blocker error, continue processing
            }

            const adBlockerError = createAdBlockerError();

            // Cache the ad blocker error immediately to prevent race conditions
            hasFinallyFailed = true;
            stripeErrorCache = adBlockerError;

            // Log to Sentry as info
            logStripeEvent('Stripe.js blocked by ad blocker', {
                loadTime,
                originalError: err.message,
            });

            // Add a small delay before showing the ad blocker error to provide better UX
            // This prevents the error from appearing instantly which can look like nothing happened
            setTimeout(() => {
                if (!mountedRef.current) {
                    return;
                }

                safeSetState(null, false, adBlockerError);
            }, STRIPE_AD_BLOCKER_RETRY_DELAY_MS);

            return true; // This was an ad blocker error, don't retry
        },
        [safeSetState, logStripeEvent],
    );

    /**
     * Main function to load Stripe with retry logic
     */
    const loadStripeWithRetry = useCallback(
        /**
         * @param {number} [attemptNumber=0] - The current attempt number (0-indexed)
         * @returns {Promise<import('@stripe/stripe-js').Stripe | undefined>} The Stripe instance or undefined if unmounted
         */
        async (attemptNumber = 0) => {
            let loadStartTime;
            const currentAttempt = attemptNumber + 1;
            const isInitialAttempt = attemptNumber === 0;
            const maxAttemptIndex = retryLimit - 1;

            // Check if we should skip loading due to cached state or existing promise
            const skipLoading = await checkShouldSkipLoading(isInitialAttempt);
            if (skipLoading) {
                return; // State already set by checkShouldSkipLoading
            }

            // Initiate loading Stripe if neither loading promise is set
            // nor we have a cached instance
            try {
                safeSetState(null, true, null);

                // Track load time right before starting to determine ad blocker issues
                loadStartTime = Date.now();

                // Ensure multiple components don't start duplicate loads
                if (!loadingPromise) {
                    loadingPromise = loadStripe(getStripePublicKey());
                }

                const stripeInstance = await loadingPromise;

                // Cache the successful instance and reset the loading promise
                stripeInstanceCache = stripeInstance;
                loadingPromise = null;

                safeSetState(stripeInstance, false, null);

                // Only log a Sentry breadcrumb if this was a retry
                if (attemptNumber > 0) {
                    logStripeEvent('Stripe.js loaded successfully after retry', {
                        attempts: currentAttempt,
                    });
                }

                return stripeInstance;
            } catch (err) {
                loadingPromise = null;

                // Guard: Check if component is unmounted
                if (!mountedRef.current) {
                    return;
                }

                // Calculate load time for ad blocker detection
                const loadTime = Date.now() - loadStartTime;

                // Handle ad blocker errors (don't retry these)
                if (handleAdBlockerError(err, loadTime)) {
                    return;
                }

                // Guard: Stop if this was the last attempt
                const isLastAttempt = attemptNumber >= maxAttemptIndex;

                if (isLastAttempt) {
                    const errorCategory = categorizeError(err);
                    hasFinallyFailed = true;
                    stripeErrorCache = err; // Cache the final error
                    safeSetState(null, false, err);
                    logStripeEvent(
                        'Stripe.js load failed after all retries',
                        {
                            error: err,
                            message: err.message,
                            attempts: currentAttempt,
                            errorCategory,
                        },
                        'error',
                        true,
                    );
                    return;
                }

                // Retry with delay for network errors or other issues
                // that might not be caused by ad blockers
                const errorCategory = categorizeError(err);
                const delay = getRetryDelay(attemptNumber);
                logStripeEvent(
                    `Stripe.js load failed (${errorCategory}), retrying in ${delay}ms`,
                    {
                        attempt: currentAttempt,
                        maxRetries: retryLimit,
                        error: err.message,
                        errorCategory,
                    },
                    'warning',
                );
                await sleep(delay);

                // Guard: Check if component is still mounted before retry
                if (!mountedRef.current) {
                    return;
                }

                return loadStripeWithRetry(attemptNumber + 1);
            }
        },
        [
            retryLimit,
            safeSetState,
            getRetryDelay,
            logStripeEvent,
            checkShouldSkipLoading,
            handleAdBlockerError,
        ],
    );

    // Entry point for loading Stripe
    // Run this on mount if loadOnMount is true, otherwise
    // we'll wait for the caller to invoke loadStripe()
    useEffect(() => {
        mountedRef.current = true;

        if (shouldStartLoading) {
            loadStripeWithRetry(0).catch(() => {
                // Error is already handled in state
            });
        }

        return () => {
            mountedRef.current = false;
        };
    }, []);

    return {
        stripe,
        loading,
        error,
        loadStripe: () => loadStripeWithRetry(0),
    };
}

/**
 * Utility function to clear the Stripe cache (useful for testing)
 * @private
 */
export const clearStripeCache = () => {
    stripeInstanceCache = null;
    loadingPromise = null;
    stripeErrorCache = null;
    hasFinallyFailed = false;
};
